/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
 */

const eventListeners = {};
const createLocaleChangeEvent = newLocale =>
    new CustomEvent('localechange', {
        detail: {
            newLocale
        }
    });

let dictionary = {};
let fallbackDictionary = {};
let locale = null;
let fallbackLocale = null;
let p11nRulesImpl = () => 0;

let dictionaryLoader = function () {
    //eslint-disable-next-line no-console
    console.warn('No dictionary loader function defined. Please define one with __.setDictionaryLoader()');
    return Promise.resolve({});
};
let p11nRulesLoader = function () {
    //eslint-disable-next-line no-console
    console.warn('Pluralization loader function is not defined. Please, define one with __.setP11nLoader()');
    return Promise.resolve(() => {});
};

/**
 * i18n placeholder.
 * TODO alias or connect to translations
 *
 * The translation key supports basic interpolation through :
 *  - %s for string
 *  - %d for numbers
 *  - %j for json (stringified)
 * Parameters are interpolated in the definition order.
 *
 * @param {String} key - the translation key in plain English
 * @param {...*} [parameters] - parameters interpolated
 * @returns {String} the translated string
 */
export function __(key = '', ...parameters) {
    let translation = dictionary[key] || fallbackDictionary[key] || key;

    if (parameters.length === 0) {
        return `${translation}`;
    }
    return parameters.reduce(
        (acc, parameter) =>
            acc.replace(/%([djs])/, match => {
                if (match === '%d') {
                    return Number(parameter);
                }
                if (match === '%j') {
                    return JSON.stringify(parameter);
                }
                return parameter;
            }),
        translation
    );
}

/**
 * Get pluralized translation
 * @param {String} key - translation key
 * @param {String} pluralKey - plural forms translation key
 * @param {Number} n - items count (needed to get correct plural variant)
 * @returns {String}
 */
__.p = (key = '', pluralKey = '', n) => {
    const message = dictionary[key] || fallbackDictionary[key] || key;
    // get plural forms from dictionary or fallback dictionary or set keywords as plural array in case of missing translation.
    // Plural will be always an array where index0 is singular, index1 is plural and so on.
    const plurals = dictionary[pluralKey] || fallbackDictionary[pluralKey] || [message, pluralKey];

    // convert result to number in case of boolean result
    const index = Number(p11nRulesImpl(n));

    const isPlural = index > 0;
    // to avoid singular response in case of empty plural translation
    const translation = plurals[index] || (isPlural ? pluralKey || message : message);

    return translation.replace('%d', n);
};

/**
 * Setup a new dictionary loader
 * @param {(locale) => Promise<object>} loader - promise should be resolved with dictionary content for the requested locale
 */
__.setDictionaryLoader = loader => {
    dictionaryLoader = loader;
};

/**
 * Setup a new pluralization rules loader
 * @param {(locale) => Promise<(choice, choices) => number>} loader - promise should be resolved with function that contains pluralization
 * rules for current locale
 */
__.setP11nRulesLoader = loader => {
    p11nRulesLoader = loader;
};

/**
 * Set locale to a new one
 * @param {string} newLocale
 * @returns {Promise<void>}
 */
__.setLocale = newLocale =>
    dictionaryLoader(newLocale).then(messages => {
        __.setDictionary(newLocale, messages);
    });

/**
 * Set new fallback locale
 * @param {string} newFallbackLocale
 * @returns {Promise<void>}
 */
__.setFallbackLocale = newFallbackLocale =>
    dictionaryLoader(newFallbackLocale).then(messages => {
        __.setFallbackDictionary(newFallbackLocale, messages);
    });

/**
 * Set new pluralization rules
 * @param {string} newLocale
 * @returns {Promise<void>}
 */
__.setP11nRules = newLocale =>
    p11nRulesLoader(newLocale)
        .then(rules => {
            if (typeof rules !== 'function') {
                throw new Error(
                    `New pluralization rules must be described as a function, but received ${typeof rules}`
                );
            }
            p11nRulesImpl = rules;
        })
        .catch(() => {
            // use default implementation
            p11nRulesImpl = () => 0;
        });

/**
 * Get actual locale
 * @returns {string|null}
 */
__.getLocale = () => locale;

/**
 * Get fallback locale
 * @returns {string|null}
 */
__.getFallbackLocale = () => fallbackLocale;

/**
 * @param {string} dictionaryLocale
 * @param {object} dictionaryContent
 */
__.setDictionary = (dictionaryLocale, dictionaryContent) => {
    const localeChangeEvent = createLocaleChangeEvent(dictionaryLocale);
    locale = dictionaryLocale;
    dictionary = dictionaryContent;
    __.dispatchEvent(localeChangeEvent);
};

/**
 * Set dictionary for fallback locale
 * @param {string} dictionaryLocale
 * @param {object} dictionaryContent
 */
__.setFallbackDictionary = (dictionaryLocale, dictionaryContent) => {
    fallbackLocale = dictionaryLocale;
    fallbackDictionary = dictionaryContent;
};

/**
 * Add event listener for provided event type
 * @param {string} type - event type
 * @param {Function} listener - event listener
 */
__.addEventListener = (type, listener) => {
    const existingListeners = eventListeners[type];
    if (!Array.isArray(existingListeners)) {
        eventListeners[type] = [];
    }
    eventListeners[type] = [...eventListeners[type], listener];
};

/**
 * Remove event listener for provided event type
 * @param {string} type - event type
 * @param {Function} listener - event listener
 */
__.removeEventListener = (type, listener) => {
    const existingListeners = eventListeners[type];
    if (!Array.isArray(existingListeners)) {
        eventListeners[type] = [];
    }
    eventListeners[type] = eventListeners[type].filter(item => item !== listener);
    // remove empty array of listeners for event
    if (eventListeners[type].length === 0) {
        delete eventListeners[type];
    }
};

/**
 * Calls event listeners for provided event
 * @param {Event} event - dispatched event
 */
__.dispatchEvent = event => {
    const { type } = event;
    const listeners = eventListeners[type];
    if (Array.isArray(listeners) && listeners.length > 0) {
        for (const listener of listeners) {
            listener(event);
        }
    }
};
